import { create, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';

import { SlidingPane } from '@holmescorp/base-widgets/sliding-pane';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { subscribe } from 'pub-sub';
import { createNlsMiddleware, lccstate, registry } from 'lcc-state';
import { delay } from '@holmescorp/common';
import { viewport } from '../support/viewport';

interface VideoPaneIcache {
	open: boolean;
	aboutToOpen: boolean;
	videonum: number | string;
}

export interface VideoPaneProperties {
	videonum: number | string;
	openaction: string;
	onClose?: () => void;
}

const icache = createICacheMiddleware<VideoPaneIcache>();
const nls = createNlsMiddleware('video');
const factory = create({
	icache,
	theme,
	nls,
	lccstate
}).properties<VideoPaneProperties>();

export const VideoPane = factory(({ properties, middleware: { icache, nls } }) => {
	icache.getOrSet('aboutToOpen', false);
	const { videonum: defaultvideo, openaction, onClose } = properties();
	icache.getOrSet('videonum', defaultvideo);
	const open = icache.getOrSet('open', () => {
		subscribe(openaction, async (topic, selectedvideo) => {
			icache.set('aboutToOpen', true);
			if (selectedvideo) {
				icache.set('videonum', selectedvideo);
			}
			await delay(200);
			icache.set('open', true);
		});
		subscribe('close-all/panes', () => {
			icache.set('open', false);
		});
		return false;
	});

	const { vw } = viewport();

	const { messages } = nls();
	const videonum = icache.get('videonum');
	const videotitle = messages[`title_${videonum}`] || ' ';
	const key = `video-${videonum}-pane`;
	const xqa = `video-pane-${open}`;

	return w(
		SlidingPane,
		{
			xqa,
			key,
			open,
			align: 'right',
			underlay: true,
			title: videotitle,
			width: Math.min(vw, 1024),
			onRequestClose: () => {
				icache.set('open', false);
				icache.set('aboutToOpen', async () => {
					await delay(500);
					return true;
				});
				onClose && onClose();
			}
		},
		[w('Video', { videonum })]
	);
});

registry.define('VideoPane', VideoPane);
